// BUILD-042 — Shared HR access/surfaces resolution.
//
// UAT visual cleanup (2026-10-05): the left sidebar now needs to know the caller's resolved HR
// surfaces (myHr/myTeam/hrAdmin) BEFORE they click into HR, so it can show the right sub-items
// (My HR / My Team / HR Administration) in a dropdown group rather than a horizontal tab row on
// the HR page itself. Previously only HrPage.tsx called GET /hr/me, and did so only when the
// caller actually navigated into /hr. This context lifts that single call up one level so
// AppLayout's sidebar and HrPage share ONE resolution instead of each resolving it separately —
// this is the same "resolve capabilities in exactly one place" principle the 2026-10-06
// reconciliation established, just relocated to cover the sidebar's new need.
//
// Behavior change this introduces: the HR access call now fires once per authenticated session
// on initial load (wherever the Provider is mounted — see App.tsx), instead of only when the
// caller visits /hr. This is a lightweight read with no side effects; nav visibility remains UX
// convenience only — every real /hr/* route still re-authorizes itself server-side regardless of
// what this resolves to (requirements AZ-10, unchanged).
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getHrMe, type HrMeResponse } from '../../api/client';

export type HrAccessState =
  | { kind: 'loading' }
  | { kind: 'denied'; message: string }
  | { kind: 'unavailable'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; me: HrMeResponse };

interface HrAccessContextValue {
  state: HrAccessState;
  /** Re-resolve from scratch: the whole HR page shows "Loading HR…" and every screen under it remounts. */
  reload: () => void;
  /**
   * Oct 20: re-read the caller's access WITHOUT leaving the screen. The screen stays mounted (so a confirmation message survives), and a failed re-read keeps
   * the access already on screen instead of downgrading it to an error. Used after an action that changes the caller's own access, such as self-setup.
   */
  refresh: () => void;
}

const HrAccessContext = createContext<HrAccessContextValue | null>(null);

export function HrAccessProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<HrAccessState>({ kind: 'loading' });

  function refresh() {
    getHrMe().then((me) => setState({ kind: 'ready', me })).catch(() => { /* keep what is already shown */ });
  }

  function load() {
    setState({ kind: 'loading' });
    getHrMe()
      .then((me) => setState({ kind: 'ready', me }))
      .catch((e: Error & { status?: number }) => {
        if (e.status === 403) {
          setState({ kind: 'denied', message: 'HR access could not be established for your account. If you believe this is incorrect, contact an HR Administrator.' });
        } else if (e.status === 503) {
          setState({ kind: 'unavailable', message: 'HR is temporarily unavailable. Please try again in a moment.' });
        } else {
          setState({ kind: 'error', message: e.message || 'Could not load HR access.' });
        }
      });
  }

  useEffect(() => { load(); }, []);

  return <HrAccessContext.Provider value={{ state, reload: load, refresh }}>{children}</HrAccessContext.Provider>;
}

export function useHrAccess(): HrAccessContextValue {
  const ctx = useContext(HrAccessContext);
  if (!ctx) throw new Error('useHrAccess must be used within an HrAccessProvider (see App.tsx)');
  return ctx;
}
